import { PermissionEnum } from '@sr-sdks/api-sdk-axios';
import {
  Button,
  Dialog,
  Icon,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@sr/ui-library';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import ProtectElement from '../components/ProtectElement';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { bankDetailsService } from '../reducers/payments/bankDetailSlice';
import { resetWorkspaceSettingsUI } from '../reducers/workspaceSettingsUI/workspaceSettingsUISlice';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import { ApiLoadingStateEnum } from '../utils/api/apiLoadingStateEnum';
import { isSuccessState } from '../utils/helpers/common';
import { MSR3BaseLayout } from './MSR3BaseLayout';

export type WorkspaceSettingsOutletContext = {
  isInviteTeammateRequested?: boolean;
  onInviteTeammateHandled?: () => void;
  registerDetailsReset?: (fn: () => void) => void;
  workspaceId?: string;
};

const SETTINGS_TABS = [
  {
    label: 'Workspace Details',
    permission: PermissionEnum.EditWorkspaceDetails,
    section: 'details' as const,
  },
  {
    label: 'Bank Details',
    permission: PermissionEnum.EditBankDetails,
    section: 'bank-account' as const,
  },
  {
    label: 'Teammates',
    permission: PermissionEnum.EditTeammates,
    section: 'teammates' as const,
  },
] as const;

type SettingsSection = (typeof SETTINGS_TABS)[number]['section'];

const primaryLeadingIcon = (icon: 'check' | 'creditCard' | 'usersThree') => (
  <Icon icon={icon} />
);

function isSettingsSection(value: string): value is SettingsSection {
  return SETTINGS_TABS.some((t) => t.section === value);
}

function settingsSectionFromPathname(pathname: string): SettingsSection {
  const raw = pathname.match(/\/manage-workspace\/([^/]+)/)?.[1];

  if (raw && isSettingsSection(raw)) return raw;

  return 'details';
}

function permissionsForSection(section: SettingsSection): PermissionEnum[] {
  const tab = SETTINGS_TABS.find((t) => t.section === section);

  return tab ? [tab.permission] : [];
}

type SettingsHeaderActionsProps = {
  bankAccountCount: number;
  bankDetailsLoadingState: ApiLoadingStateEnum;
  /** FR-017: whether the details form has unsaved changes — drives the Save button disabled state. */
  isDetailsDirty: boolean;
  /** FR-017: whether the details mutation is in-flight — drives the Save button loading state. */
  isDetailsPending: boolean;
  onAddBankAccountClick: () => void;
  /** FR-021: discards unsaved changes by calling the section's registered reset function. */
  onDiscard: () => void;
  onInviteTeammateClick: () => void;
  section: SettingsSection;
  workspaceId?: string;
};

function SettingsHeaderActions({
  bankAccountCount,
  bankDetailsLoadingState,
  isDetailsDirty,
  isDetailsPending,
  onAddBankAccountClick,
  onDiscard,
  onInviteTeammateClick,
  section,
  workspaceId,
}: SettingsHeaderActionsProps) {
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  // Called unconditionally (never inside the switch) so the hook order stays
  // stable across sections. Same reasoning as useVisibleBankDetails: `data`
  // is the only proof the flag value is known, so a settled query without it
  // is unavailable rather than merely "not enabled" -- unknown must never
  // grant, so the bank-account case below withholds its button until this
  // resolves.
  const [isMultiBankAccountEnabled, multiBankFeaturesQuery] = useFeatureFlag(
    FEATURE_FLAGS.MULTI_BANK_ACCOUNT,
  );
  const isMultiBankFlagResolved = multiBankFeaturesQuery.data !== undefined;

  const withSectionPermission = (node: ReactNode) => (
    <ProtectElement
      permissionRequired={permissionsForSection(section)}
      workspaceId={workspaceId}
    >
      {node}
    </ProtectElement>
  );

  switch (section) {
    case 'details':
      return withSectionPermission(
        /*
         * FR-016: the Save button is in the PageHeader, not inside the form.
         * `form="workspace-details-form"` links it to the form element in WorkspaceDetailsForm
         * via the HTML5 form attribute — no prop-drilling of the submit handler needed.
         * FR-017: disabled when form is clean; enabled as soon as any field changes.
         * T054: Discard button resets the form via the registered reset callback.
         */
        <div className="flex items-center gap-2">
          <Button
            disabled={!isDetailsDirty}
            form="workspace-details-form"
            loading={isDetailsPending}
            sectionLeft={primaryLeadingIcon('check')}
            size="sm"
            type="submit"
          >
            Update Details
          </Button>
          <Button
            disabled={!isDetailsDirty || isDetailsPending}
            size="sm"
            variant="outline"
            onClick={() => setIsDiscardDialogOpen(true)}
          >
            Revert Changes
          </Button>

          <Dialog
            heading="Are you sure you want to revert changes?"
            actionButtonProps={{
              children: 'Revert Changes',
              onClick: () => {
                onDiscard();
                setIsDiscardDialogOpen(false);
              },
            }}
            open={isDiscardDialogOpen}
            onOpenChange={setIsDiscardDialogOpen}
          >
            <div className="font-sans text-base leading-normal">
              This will revert any changes you’ve made to since the last time
              update.
            </div>
          </Dialog>
        </div>,
      );
    case 'bank-account': {
      // With multi-bank off a workspace may hold only one account, so the
      // flag-off label may only appear on a *confirmed* empty list — idle,
      // loading and failed all leave `bankAccountCount` at 0 while accounts
      // may well exist. The flag-on label carries no such condition. Unknown
      // never grants, so until the flag itself resolves, neither label
      // renders.
      const canShowFlagOffButton =
        isSuccessState(bankDetailsLoadingState) && bankAccountCount === 0;
      const shouldShowAddBankAccountButton =
        isMultiBankFlagResolved &&
        (isMultiBankAccountEnabled || canShowFlagOffButton);

      return withSectionPermission(
        shouldShowAddBankAccountButton ? (
          <Button
            sectionLeft={primaryLeadingIcon('creditCard')}
            size="sm"
            onClick={onAddBankAccountClick}
          >
            {isMultiBankAccountEnabled
              ? 'Add New Bank Account'
              : 'Add Bank Account'}
          </Button>
        ) : null,
      );
    }
    case 'teammates':
      return withSectionPermission(
        <Button
          sectionLeft={primaryLeadingIcon('usersThree')}
          size="sm"
          onClick={onInviteTeammateClick}
        >
          Invite Teammates
        </Button>,
      );
  }
}

/**
 * MSR3 shell for Workspace Settings: {@link PageHeader}, segmented tab navigation, and outlet for tab content.
 */
export const WorkspaceSettingsLayout = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [isInviteTeammateRequested, setIsInviteTeammateRequested] =
    useState(false);

  // Stable identity so InvitedTable's open-trigger effect doesn't re-run on
  // every layout render.
  const handleInviteTeammateHandled = useCallback(
    () => setIsInviteTeammateRequested(false),
    [],
  );

  const dispatch = useAppDispatch();
  // FR-017: dirty/pending state read from Redux — written by the active section's form.
  const isDetailsDirty = useTypedSelector(
    (s) => s.workspaceSettingsUI.isDetailsDirty,
  );
  const isDetailsPending = useTypedSelector(
    (s) => s.workspaceSettingsUI.isDetailsPending,
  );
  const bankAccountCount = useTypedSelector(
    (s) => bankDetailsService.selectors.bankDetails(s).length,
  );
  const bankDetailsLoadingState = useTypedSelector(
    bankDetailsService.selectors.isLoading,
  );

  // Reset UI state on unmount so stale state doesn't persist across navigations.
  useEffect(
    () => () => {
      dispatch(resetWorkspaceSettingsUI());
    },
    [dispatch],
  );

  // T053: reset callback registered by the active section so the Discard button can reset the form.
  // useRef — not useState — because we never render based on this value; onClick reads it imperatively.
  const detailsResetRef = useRef<(() => void) | null>(null);
  const registerDetailsReset = useCallback((fn: () => void) => {
    detailsResetRef.current = fn;
  }, []);

  const tabLink = (segment: string) =>
    `/workspace/${workspaceId!}/manage-workspace/${segment}`;

  const section = settingsSectionFromPathname(location.pathname);
  const handleAddBankAccountClick = () => {
    setSearchParams(
      (current) => {
        current.set('bankAccountForm', 'new');

        return current;
      },
      { replace: true },
    );
  };

  return (
    <MSR3BaseLayout
      pageHeader={
        <PageHeader
          title="Workspace Settings"
          actions={
            <SettingsHeaderActions
              bankAccountCount={bankAccountCount}
              bankDetailsLoadingState={bankDetailsLoadingState}
              isDetailsDirty={isDetailsDirty}
              isDetailsPending={isDetailsPending}
              section={section}
              workspaceId={workspaceId}
              onAddBankAccountClick={handleAddBankAccountClick}
              onDiscard={() => detailsResetRef.current?.()}
              onInviteTeammateClick={() => setIsInviteTeammateRequested(true)}
            />
          }
        >
          <Tabs
            className="w-fit"
            value={section}
            onValueChange={(value) => navigate(tabLink(value))}
          >
            <TabsList>
              {SETTINGS_TABS.map(
                ({ label, permission, section: tabSection }) => (
                  <ProtectElement
                    key={tabSection}
                    permissionRequired={[permission]}
                    workspaceId={workspaceId}
                  >
                    <TabsTrigger value={tabSection}>{label}</TabsTrigger>
                  </ProtectElement>
                ),
              )}
            </TabsList>
          </Tabs>
        </PageHeader>
      }
    >
      <div>
        <Outlet
          context={{
            isInviteTeammateRequested,
            onInviteTeammateHandled: handleInviteTeammateHandled,
            registerDetailsReset,
            workspaceId,
          }}
        />
      </div>
    </MSR3BaseLayout>
  );
};
